import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Edit2, Trash2, FileMinus, Link2 } from "lucide-react";
import { ProveedorCombobox } from "@/components/ProveedorCombobox";
import { ProductoCombobox } from "@/components/ProductoCombobox";

const IVA_OPTIONS = [
  { value: "0", label: "0% (Exento)" },
  { value: "5", label: "5%" },
  { value: "12", label: "12%" },
  { value: "15", label: "15%" },
];

export default function NotasCreditoProveedor() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [proveedores, setProveedores] = useState([]);
  const [cuentas, setCuentas] = useState([]);
  const [filtroProv, setFiltroProv] = useState("");
  const [filtroEstado, setFiltroEstado] = useState("");
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 20;

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  // Facturas del proveedor seleccionado y detalle de la factura escogida
  const [facturasProv, setFacturasProv] = useState([]);
  const [facturaLineas, setFacturaLineas] = useState([]); // líneas de la factura seleccionada
  const [seleccionLineas, setSeleccionLineas] = useState({}); // idx → { checked, cantidad }
  // ITER189 (Feb 2026): líneas manuales agregadas por el usuario (para NCs sobre
  // facturas históricas sin líneas registradas o cuando se necesita bajar
  // productos que no están en la factura original).
  const [manualLineas, setManualLineas] = useState([]);
  const [productos, setProductos] = useState([]);
  const [lotesPorProducto, setLotesPorProducto] = useState({});

  function emptyForm() {
    return {
      proveedor_id: "",
      numero: "",
      fecha: new Date().toISOString().slice(0, 10),
      motivo: "",
      modo: "ajuste", // ajuste | producto
      factura_compra_id: "",
      factura_compra_numero: "",
      gasto_id: "",            // ITER141: NC contra gasto operativo
      doc_origen_tipo: "",     // 'factura' | 'gasto' (para el Select del frontend)
      cuenta_contable_id: "",
      lineas: [],
      subtotal: 0,
      iva_porc: "12",
      iva: 0,
      total: 0,
      observaciones: "",
    };
  }

  const load = async () => {
    try {
      const params = { skip: page * PAGE_SIZE, limit: PAGE_SIZE };
      if (filtroProv) params.proveedor_id = filtroProv;
      if (filtroEstado) params.estado = filtroEstado;
      const { data } = await api.get("/accounting/notas-credito-proveedor", { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const loadCatalogos = async () => {
    try {
      const [pr, cc, prod] = await Promise.all([
        api.get("/masters/proveedores"),
        api.get("/masters/cuentas-auxiliares"),
        api.get("/masters/productos-comerciales?activo=true&limit=2000").catch(() => ({ data: { items: [] } })),
      ]);
      setProveedores(pr.data); setCuentas(cc.data || []);
      const prodItems = Array.isArray(prod.data) ? prod.data : (prod.data?.items || []);
      setProductos(prodItems);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // ITER189: cargar lotes disponibles de un producto para el selector manual
  const loadLotesProducto = async (producto_id) => {
    if (lotesPorProducto[producto_id]) return lotesPorProducto[producto_id];
    try {
      const { data } = await api.get(`/masters/productos-comerciales/${producto_id}/lotes-disponibles`);
      const lotes = Array.isArray(data) ? data : (data?.items || []);
      setLotesPorProducto(prev => ({ ...prev, [producto_id]: lotes }));
      return lotes;
    } catch (e) {
      return [];
    }
  };

  const addLineaManual = () => {
    setManualLineas(arr => [...arr, {
      producto_comercial_id: "", producto_nombre: "",
      lote_id: "", numero_lote: "", bodega_id: "",
      cantidad: 1, precio_unitario: 0,
    }]);
  };
  const removeLineaManual = (idx) => {
    setManualLineas(arr => arr.filter((_, i) => i !== idx));
  };
  const updateLineaManual = (idx, patch) => {
    setManualLineas(arr => arr.map((l, i) => i === idx ? { ...l, ...patch } : l));
  };
  useEffect(() => { loadCatalogos(); }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, filtroProv, filtroEstado]);

  // Al cambiar proveedor en el formulario, cargar sus facturas + gastos (solo en creación)
  useEffect(() => {
    if (editing) return;
    if (!form.proveedor_id) { setFacturasProv([]); setFacturaLineas([]); setSeleccionLineas({}); return; }
    (async () => {
      try {
        const { data } = await api.get(`/accounting/notas-credito-proveedor/facturas-proveedor/${form.proveedor_id}`);
        setFacturasProv(data || []);
      } catch (e) { toast.error(formatApiError(e)); setFacturasProv([]); }
    })();
    // limpiar selección de factura previa
    setFacturaLineas([]); setSeleccionLineas({});
    setForm(f => ({ ...f, factura_compra_id: "", factura_compra_numero: "", gasto_id: "", doc_origen_tipo: "" }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.proveedor_id]);

  // Al elegir factura, cargar detalle (líneas/productos). Para gastos no hay líneas.
  useEffect(() => {
    if (editing) return;
    if (!form.factura_compra_id) { setFacturaLineas([]); setSeleccionLineas({}); return; }
    (async () => {
      try {
        const { data } = await api.get(`/accounting/notas-credito-proveedor/factura-detalle/${form.factura_compra_id}`);
        setFacturaLineas(data?.lineas || []);
        setSeleccionLineas({});
      } catch (e) { toast.error(formatApiError(e)); setFacturaLineas([]); }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.factura_compra_id]);

  // Recalcular subtotal/iva/total al cambiar selección o IVA (ITER189: también en modo edición)
  useEffect(() => {
    if (form.modo !== "producto") return;
    const lineasSel = Object.entries(seleccionLineas)
      .filter(([, v]) => v.checked && Number(v.cantidad) > 0)
      .map(([idx, v]) => {
        const fl = facturaLineas[Number(idx)] || {};
        const cant = Number(v.cantidad) || 0;
        const pu = Number(fl.costo_unitario || fl.precio_unitario || 0);
        return cant * pu;
      });
    const manualSubs = manualLineas
      .filter(l => Number(l.cantidad) > 0 && Number(l.precio_unitario) >= 0)
      .map(l => Number(l.cantidad) * Number(l.precio_unitario));
    const subtotal = [...lineasSel, ...manualSubs].reduce((a, b) => a + b, 0);
    const ivaPorc = Number(form.iva_porc || 0);
    const ivaMonto = +(subtotal * ivaPorc / 100).toFixed(2);
    const totalCalc = +(subtotal + ivaMonto).toFixed(2);
    setForm(f => ({ ...f, subtotal: +subtotal.toFixed(2), iva: ivaMonto, total: totalCalc }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seleccionLineas, form.iva_porc, form.modo, facturaLineas, manualLineas]);

  // Recalcular IVA cuando es modo 'ajuste' al cambiar subtotal o iva_porc
  useEffect(() => {
    if (editing) return;
    if (form.modo !== "ajuste") return;
    const subtotal = Number(form.subtotal || 0);
    const ivaPorc = Number(form.iva_porc || 0);
    const ivaMonto = +(subtotal * ivaPorc / 100).toFixed(2);
    setForm(f => ({ ...f, iva: ivaMonto, total: +(subtotal + ivaMonto).toFixed(2) }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.subtotal, form.iva_porc, form.modo]);

  const toggleLinea = (idx, checked) => {
    const fl = facturaLineas[idx];
    setSeleccionLineas(prev => ({
      ...prev,
      [idx]: {
        checked,
        cantidad: prev[idx]?.cantidad ?? Number(fl?.cantidad || 1),
      },
    }));
  };
  const setCantLinea = (idx, cantidad) => {
    setSeleccionLineas(prev => ({
      ...prev,
      [idx]: { checked: prev[idx]?.checked ?? true, cantidad },
    }));
  };

  const save = async () => {
    if (!form.proveedor_id || !form.numero || !form.fecha) {
      toast.error("Proveedor, número y fecha son obligatorios");
      return;
    }
    if (!form.total || Number(form.total) <= 0) {
      toast.error("El total debe ser mayor a 0");
      return;
    }
    try {
      let lineasPayload = [];
      if (form.modo === "producto") {
        // 1) Líneas seleccionadas de la factura
        lineasPayload = Object.entries(seleccionLineas)
          .filter(([, v]) => v.checked && Number(v.cantidad) > 0)
          .map(([idx, v]) => {
            const fl = facturaLineas[Number(idx)] || {};
            const cant = Number(v.cantidad) || 0;
            const pu = Number(fl.costo_unitario || fl.precio_unitario || 0);
            const sub = +(cant * pu).toFixed(2);
            return {
              producto_comercial_id: fl.producto_comercial_id || null,
              codigo_interno: fl.codigo_interno || null,
              producto_nombre: fl.producto_nombre || fl.descripcion || null,
              lote_id: fl.lote_id || null,
              numero_lote: fl.numero_lote || null,
              cantidad: cant,
              precio_unitario: pu,
              subtotal: sub,
              iva_porc: Number(form.iva_porc || 0),
              iva_monto: +(sub * Number(form.iva_porc || 0) / 100).toFixed(2),
            };
          });
        // 2) ITER189: Líneas manuales agregadas por el usuario
        const manualPayload = manualLineas
          .filter(l => l.producto_comercial_id && Number(l.cantidad) > 0 && l.lote_id)
          .map(l => {
            const cant = Number(l.cantidad) || 0;
            const pu = Number(l.precio_unitario) || 0;
            const sub = +(cant * pu).toFixed(2);
            return {
              producto_comercial_id: l.producto_comercial_id,
              producto_nombre: l.producto_nombre,
              lote_id: l.lote_id,
              numero_lote: l.numero_lote || null,
              cantidad: cant,
              precio_unitario: pu,
              subtotal: sub,
              iva_porc: Number(form.iva_porc || 0),
              iva_monto: +(sub * Number(form.iva_porc || 0) / 100).toFixed(2),
            };
          });
        lineasPayload = [...lineasPayload, ...manualPayload];
        if (lineasPayload.length === 0) {
          toast.error("Selecciona al menos un producto de la factura o agrega una línea manual");
          return;
        }
      }

      const payload = { ...form, lineas: lineasPayload };
      ["factura_compra_id", "gasto_id", "cuenta_contable_id"].forEach(k => { if (!payload[k]) payload[k] = null; });
      payload.subtotal = parseFloat(payload.subtotal) || 0;
      payload.iva = parseFloat(payload.iva) || 0;
      payload.total = parseFloat(payload.total) || 0;
      // Adjuntar número del documento aplicado si lo tenemos
      if (payload.factura_compra_id) {
        const facSel = facturasProv.find(x => x.id === payload.factura_compra_id && x.tipo === "factura");
        if (facSel) payload.factura_compra_numero = facSel.numero_factura;
      }
      // Quitar campos de UI que no son del backend
      delete payload.doc_origen_tipo;

      if (editing) {
        // ITER189: enviar todos los campos editables
        await api.patch(`/accounting/notas-credito-proveedor/${editing.id}`, {
          proveedor_id: payload.proveedor_id,
          numero: payload.numero,
          motivo: payload.motivo,
          fecha: payload.fecha,
          modo: payload.modo,
          factura_compra_id: payload.factura_compra_id,
          factura_compra_numero: payload.factura_compra_numero,
          gasto_id: payload.gasto_id,
          cuenta_contable_id: payload.cuenta_contable_id,
          lineas: form.modo === "producto" ? lineasPayload : (payload.lineas || []),
          subtotal: payload.subtotal,
          iva: payload.iva,
          total: payload.total,
          observaciones: payload.observaciones,
        });
        toast.success("Actualizada");
      } else {
        await api.post("/accounting/notas-credito-proveedor", payload);
        toast.success("Nota de crédito creada");
      }
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (it) => {
    // ITER194 (Feb 2026): permitir eliminar incluso NCs aplicadas — revierte todo (stock, CxP, gasto, asiento contable)
    const aplicada = it.estado === "aplicada";
    let msg = `¿Eliminar NC ${it.numero}?`;
    if (aplicada) {
      msg = `⚠ Esta NC YA FUE APLICADA a ${it.factura_compra_numero_aplicada || "un gasto"}.\n\nAl eliminarla se revierte TODO:\n• Stock devuelto vuelve a los lotes\n• CxP recupera el saldo pendiente\n• Se elimina el asiento contable\n• No queda registro de la NC\n\n¿Continuar con la eliminación?`;
    }
    if (!window.confirm(msg)) return;
    try {
      const url = `/accounting/notas-credito-proveedor/${it.id}${aplicada ? "?forzar=true" : ""}`;
      const { data } = await api.delete(url);
      const det = data?.detalle || {};
      let toastMsg = `NC ${det.nc_numero || it.numero} eliminada`;
      const partes = [];
      if (det.stock_restaurado?.length) partes.push(`stock restaurado en ${det.stock_restaurado.length} lote(s)`);
      if (det.cxp_revertida) partes.push(`CxP saldo: $${det.cxp_revertida.saldo_despues?.toFixed(2)}`);
      if (det.gasto_revertido) partes.push("gasto revertido");
      if (det.asiento_eliminado) partes.push("asiento contable borrado");
      if (partes.length) toastMsg += ` — ${partes.join(", ")}`;
      toast.success(toastMsg, { duration: 6000 });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const aplicarCxp = async (it) => {
    const facturaId = window.prompt(`ID de factura de compra para aplicar NC ${it.numero}:`);
    if (!facturaId) return;
    try {
      const { data } = await api.post(`/accounting/notas-credito-proveedor/${it.id}/aplicar-cxp?factura_compra_id=${encodeURIComponent(facturaId)}`);
      toast.success(`Aplicada. Saldo CxP: $${data.cxp_saldo.toFixed(2)}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const provById = useMemo(() => Object.fromEntries(proveedores.map(p => [p.id, p.razon_social])), [proveedores]);

  const estadoBadge = (e) => ({
    registrada: <Badge variant="outline" className="text-[10px]">Registrada</Badge>,
    aplicada: <Badge className="bg-[#205c45] text-white text-[10px]">Aplicada</Badge>,
    autorizado: <Badge className="bg-[#2d7aa8] text-white text-[10px]">Autorizado</Badge>,
    anulada: <Badge variant="destructive" className="text-[10px]">Anulada</Badge>,
  }[e] || <Badge variant="outline">{e}</Badge>);

  return (
    <div className="space-y-5" data-testid="nc-proveedor-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#5c7368] mb-1">Contabilidad</div>
          <h1 className="text-3xl font-bold tracking-tight text-[#15241d]" style={{ fontFamily: "'Manrope', sans-serif" }}>
            <FileMinus className="inline mr-2 mb-1" size={28} />
            Notas de Crédito Proveedor
          </h1>
          <p className="text-sm text-[#4a5c53] mt-1">Documentos recibidos de proveedores que reducen su CxP (devoluciones, ajustes, descuentos).</p>
        </div>
        <Button onClick={() => { setEditing(null); setForm(emptyForm()); setManualLineas([]); setOpen(true); }} className="bg-[#205c45] hover:bg-[#1a4a37]" data-testid="create-nc-prov-btn">
          <Plus size={16} className="mr-2" /> Nueva NC
        </Button>
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap gap-2 items-end p-3 rounded-md border border-[#d1dbd6] bg-white">
        <div className="flex-1 min-w-[260px]">
          <Label className="text-[10px] uppercase">Proveedor</Label>
          <ProveedorCombobox
            value={filtroProv}
            onChange={(v) => { setFiltroProv(v); setPage(0); }}
            proveedores={[{ id: "", razon_social: "Todos los proveedores" }, ...proveedores]}
            placeholder="Todos los proveedores"
            testId="filter-prov"
          />
        </div>
        <div>
          <Label className="text-[10px] uppercase">Estado</Label>
          <select value={filtroEstado} onChange={(e) => { setFiltroEstado(e.target.value); setPage(0); }} className="h-9 px-2 rounded border border-[#d1dbd6] text-sm bg-white" data-testid="filter-estado">
            <option value="">Todos</option>
            <option value="registrada">Registrada</option>
            <option value="aplicada">Aplicada</option>
            <option value="autorizado">Autorizado (migrado)</option>
            <option value="anulada">Anulada</option>
          </select>
        </div>
        <div className="text-xs text-[#5c7368] ml-auto self-center">
          {total} totales · página {page + 1} de {Math.max(1, Math.ceil(total / PAGE_SIZE))}
        </div>
      </div>

      <div className="rounded-md border border-[#d1dbd6] bg-white overflow-hidden">
        <table className="erp-table">
          <thead>
            <tr>
              <th>Número</th><th>Fecha</th><th>Proveedor</th><th>Motivo</th>
              <th>Factura aplicada</th><th className="text-right">Total</th><th>Estado</th>
              <th className="w-32"></th>
            </tr>
          </thead>
          <tbody>
            {items.map(it => (
              <tr key={it.id} data-testid={`nc-prov-row-${it.id}`}>
                <td className="font-mono text-xs">{it.numero}</td>
                <td className="text-xs">{it.fecha}</td>
                <td className="font-medium">{it.proveedor_nombre || provById[it.proveedor_id] || "—"}</td>
                <td className="text-xs">{it.motivo || "—"}</td>
                <td className="text-xs font-mono">{it.factura_compra_numero_aplicada || "—"}</td>
                <td className="text-right tabular-nums font-semibold">${(it.total || 0).toFixed(2)}</td>
                <td>{estadoBadge(it.estado)}</td>
                <td>
                  <div className="flex gap-1 justify-end">
                    {it.estado !== "aplicada" && it.estado !== "autorizado" && (
                      <Button size="icon" variant="ghost" onClick={() => aplicarCxp(it)} title="Aplicar a CxP" data-testid={`aplicar-${it.id}`}>
                        <Link2 size={14} className="text-[#205c45]" />
                      </Button>
                    )}
                    <Button size="icon" variant="ghost" onClick={() => {
                      setEditing(it);
                      setForm({ ...emptyForm(), ...it, iva_porc: String(it.lineas?.[0]?.iva_porc ?? 12) });
                      // ITER189: precargar líneas manuales para editar
                      setManualLineas((it.lineas || []).map(l => ({
                        producto_comercial_id: l.producto_comercial_id || "",
                        producto_nombre: l.producto_nombre || "",
                        lote_id: l.lote_id || "",
                        numero_lote: l.numero_lote || "",
                        bodega_id: l.bodega_id || "",
                        cantidad: l.cantidad || 1,
                        precio_unitario: l.precio_unitario || 0,
                      })));
                      setFacturaLineas([]);
                      setSeleccionLineas({});
                      setOpen(true);
                    }}><Edit2 size={14} /></Button>
                    {it.estado !== "aplicada" ? (
                      <Button size="icon" variant="ghost" onClick={() => remove(it)} title="Eliminar NC" data-testid={`nc-prov-delete-${it.id}`}>
                        <Trash2 size={14} className="text-[#b53a25]" />
                      </Button>
                    ) : (
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => remove(it)}
                        title="Eliminar NC aplicada (revierte stock, CxP, gasto y asiento contable)"
                        data-testid={`nc-prov-delete-force-${it.id}`}
                        className="hover:bg-[#fbe4d8]"
                      >
                        <Trash2 size={14} className="text-[#b53a25]" />
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={8} className="text-center py-10 text-[#8ba397]">Sin notas de crédito</td></tr>}
          </tbody>
        </table>
      </div>

      {total > PAGE_SIZE && (
        <div className="flex justify-center items-center gap-4 text-xs pb-12">
          <Button size="sm" variant="outline" disabled={page === 0} onClick={() => setPage(p => Math.max(0, p - 1))} data-testid="page-prev">← Anterior</Button>
          <span className="text-[#5c7368]">Mostrando {page * PAGE_SIZE + 1}–{Math.min(total, (page + 1) * PAGE_SIZE)} de {total}</span>
          <Button size="sm" variant="outline" disabled={(page + 1) * PAGE_SIZE >= total} onClick={() => setPage(p => p + 1)} data-testid="page-next">Siguiente →</Button>
        </div>
      )}

      {/* Dialog Crear/Editar */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Editar" : "Nueva"} Nota de Crédito Proveedor</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Proveedor *</Label>
              {editing ? (
                <Input value={provById[form.proveedor_id] || ""} disabled />
              ) : (
                <ProveedorCombobox
                  value={form.proveedor_id}
                  onChange={(v) => setForm(f => ({...f, proveedor_id: v}))}
                  proveedores={proveedores}
                  placeholder="Buscar proveedor por razón social, RUC…"
                  testId="nc-prov"
                />
              )}
            </div>
            <div><Label>Número documento *</Label><Input value={form.numero} onChange={(e) => setForm(f => ({...f, numero: e.target.value}))} disabled={!!editing} data-testid="nc-numero" /></div>
            <div><Label>Fecha *</Label><Input type="date" value={form.fecha} onChange={(e) => setForm(f => ({...f, fecha: e.target.value}))} /></div>
            <div>
              <Label>Modo</Label>
              <Select value={form.modo} onValueChange={(v) => setForm(f => ({...f, modo: v}))} disabled={!!editing || form.doc_origen_tipo === "gasto"}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ajuste">Ajuste / Descuento</SelectItem>
                  <SelectItem value="producto" disabled={form.doc_origen_tipo === "gasto"}>Devolución de producto</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Factura / Gasto del proveedor</Label>
              {editing ? (
                <Input value={form.factura_compra_numero_aplicada || form.factura_compra_numero || form.gasto_numero || "—"} disabled />
              ) : (
                <Select
                  value={form.doc_origen_tipo && (form.factura_compra_id || form.gasto_id)
                    ? `${form.doc_origen_tipo}:${form.factura_compra_id || form.gasto_id}`
                    : "none"}
                  onValueChange={(v) => {
                    if (v === "none") {
                      setForm(f => ({ ...f, factura_compra_id: "", gasto_id: "", doc_origen_tipo: "" }));
                      return;
                    }
                    const [tipo, id] = v.split(":");
                    if (tipo === "factura") {
                      setForm(f => ({ ...f, factura_compra_id: id, gasto_id: "", doc_origen_tipo: "factura" }));
                    } else if (tipo === "gasto") {
                      // gastos no permiten modo producto
                      setForm(f => ({ ...f, factura_compra_id: "", gasto_id: id, doc_origen_tipo: "gasto", modo: "ajuste" }));
                    }
                  }}
                  disabled={!form.proveedor_id}
                >
                  <SelectTrigger data-testid="nc-factura-select">
                    <SelectValue placeholder={form.proveedor_id ? "Seleccionar factura o gasto…" : "Selecciona primero el proveedor"} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Sin documento asociado —</SelectItem>
                    {facturasProv.filter(f => f.tipo === "factura").length > 0 && (
                      <div className="px-2 py-1 text-[10px] uppercase font-semibold text-[#5c7368] bg-[#f7f9f8]">Facturas de compra</div>
                    )}
                    {facturasProv.filter(f => f.tipo === "factura").map((f) => (
                      <SelectItem key={`factura:${f.id}`} value={`factura:${f.id}`}>
                        {f.numero_factura} · {f.fecha_factura} · ${Number(f.total || 0).toFixed(2)}
                        {f.cxp_saldo != null ? ` · Saldo: $${Number(f.cxp_saldo).toFixed(2)}` : ""}
                      </SelectItem>
                    ))}
                    {facturasProv.filter(f => f.tipo === "gasto").length > 0 && (
                      <div className="px-2 py-1 text-[10px] uppercase font-semibold text-[#5c7368] bg-[#f7f9f8] mt-1">Gastos operativos</div>
                    )}
                    {facturasProv.filter(f => f.tipo === "gasto").map((g) => (
                      <SelectItem key={`gasto:${g.id}`} value={`gasto:${g.id}`}>
                        {g.numero_factura} · {g.fecha_factura} · ${Number(g.total || 0).toFixed(2)}
                        {g.categoria_nombre ? ` · ${g.categoria_nombre}` : ""}
                        {g.nc_aplicada_monto ? ` · NC aplicada: $${Number(g.nc_aplicada_monto).toFixed(2)}` : ""}
                      </SelectItem>
                    ))}
                    {form.proveedor_id && facturasProv.length === 0 && (
                      <div className="p-2 text-xs text-[#8ba397]">Este proveedor no tiene facturas ni gastos registrados</div>
                    )}
                  </SelectContent>
                </Select>
              )}
              {form.doc_origen_tipo === "gasto" && (
                <div className="text-[10px] text-[#5c7368] mt-1">
                  ℹ Gastos operativos solo permiten modo &quot;Ajuste / Descuento&quot;.
                </div>
              )}
            </div>
            <div className="col-span-2">
              <Label>Motivo *</Label>
              <Input value={form.motivo} onChange={(e) => setForm(f => ({...f, motivo: e.target.value}))} placeholder="Ej: Producto vencido, error facturación..." data-testid="nc-motivo" />
            </div>

            {/* Selector de productos (sólo modo producto y en creación — al editar se usa la sección manual de abajo) */}
            {!editing && form.modo === "producto" && form.factura_compra_id && (
              <div className="col-span-2 border-t pt-3">
                <Label className="mb-2 inline-block">Productos a devolver de la factura seleccionada</Label>
                {facturaLineas.length === 0 ? (
                  <div className="text-xs text-[#8ba397] py-3">La factura no tiene líneas de productos</div>
                ) : (
                  <div className="rounded border border-[#d1dbd6] overflow-x-auto bg-white">
                    <table className="erp-table text-xs">
                      <thead>
                        <tr>
                          <th className="w-10"></th>
                          <th>Producto</th>
                          <th>Lote</th>
                          <th className="text-right">Cant. factura</th>
                          <th className="text-right">Costo unit.</th>
                          <th className="text-right w-28">Cant. a devolver</th>
                          <th className="text-right">Subtotal</th>
                        </tr>
                      </thead>
                      <tbody>
                        {facturaLineas.map((fl, idx) => {
                          const sel = seleccionLineas[idx] || {};
                          const checked = !!sel.checked;
                          const cant = Number(sel.cantidad ?? fl.cantidad) || 0;
                          const pu = Number(fl.costo_unitario || fl.precio_unitario || 0);
                          const sub = checked ? cant * pu : 0;
                          return (
                            <tr key={idx} className={checked ? "bg-[#f3f8f5]" : ""}>
                              <td>
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={(e) => toggleLinea(idx, e.target.checked)}
                                  data-testid={`nc-linea-check-${idx}`}
                                />
                              </td>
                              <td>
                                <div className="font-medium">{fl.producto_nombre || fl.descripcion || "—"}</div>
                                {fl.codigo_interno && <div className="text-[10px] text-[#8ba397] font-mono">{fl.codigo_interno}</div>}
                              </td>
                              <td className="text-xs">{fl.numero_lote || "—"}</td>
                              <td className="text-right tabular-nums">{Number(fl.cantidad || 0).toFixed(0)}</td>
                              <td className="text-right tabular-nums">${pu.toFixed(4)}</td>
                              <td className="text-right">
                                <Input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  max={fl.cantidad}
                                  value={sel.cantidad ?? fl.cantidad}
                                  disabled={!checked}
                                  onChange={(e) => setCantLinea(idx, e.target.value)}
                                  className="h-7 w-20 text-right tabular-nums"
                                  data-testid={`nc-linea-cant-${idx}`}
                                />
                              </td>
                              <td className="text-right tabular-nums font-semibold">${sub.toFixed(2)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* ITER189: Sección de líneas manuales (para NC sobre facturas históricas sin líneas o productos adicionales) */}
            {form.modo === "producto" && (
              <div className="col-span-2 border-t pt-3" data-testid="nc-manual-lineas-section">
                <div className="flex items-center justify-between mb-2">
                  <Label className="mb-0">Añadir productos manualmente (bajarán stock del lote seleccionado)</Label>
                  <Button size="sm" variant="outline" onClick={addLineaManual} data-testid="nc-add-manual-btn">
                    <Plus size={12} className="mr-1" /> Añadir línea
                  </Button>
                </div>
                {manualLineas.length === 0 ? (
                  <div className="text-xs text-[#8ba397] py-2">
                    {editing ? "Sin líneas registradas. Haz clic en 'Añadir línea' para agregar productos." : "Útil cuando la factura no tiene productos registrados (histórica) o quieres añadir productos adicionales."}
                  </div>
                ) : (
                  <div className="space-y-2">
                    {manualLineas.map((l, idx) => {
                      const lotes = lotesPorProducto[l.producto_comercial_id] || [];
                      return (
                        <div key={idx} className="grid grid-cols-12 gap-2 items-end border border-[#d1dbd6] rounded p-2 bg-[#fafdfc]" data-testid={`nc-manual-linea-${idx}`}>
                          <div className="col-span-4">
                            <Label className="text-[10px]">Producto</Label>
                            <ProductoCombobox
                              value={l.producto_comercial_id}
                              productos={productos}
                              placeholder="Buscar producto…"
                              onChange={async (v) => {
                                const p = productos.find(x => x.id === v);
                                updateLineaManual(idx, {
                                  producto_comercial_id: v,
                                  producto_nombre: p?.nombre_comercial || p?.descripcion || "",
                                  lote_id: "", numero_lote: "", bodega_id: "",
                                  precio_unitario: p?.costo_unidad || p?.precio_costo || 0,
                                });
                                if (v) await loadLotesProducto(v);
                              }}
                              testId={`nc-manual-prod-${idx}`}
                            />
                          </div>
                          <div className="col-span-3">
                            <Label className="text-[10px]">Lote (bodega)</Label>
                            <Select
                              value={l.lote_id || "__none__"}
                              onValueChange={(v) => {
                                if (v === "__none__") { updateLineaManual(idx, { lote_id: "", numero_lote: "", bodega_id: "" }); return; }
                                const lot = lotes.find(x => x.id === v);
                                updateLineaManual(idx, {
                                  lote_id: v,
                                  numero_lote: lot?.numero_lote || "",
                                  bodega_id: lot?.bodega_id || "",
                                });
                              }}
                              disabled={!l.producto_comercial_id}
                            >
                              <SelectTrigger className="h-8 text-xs" data-testid={`nc-manual-lote-${idx}`}>
                                <SelectValue placeholder={l.producto_comercial_id ? (lotes.length ? "Elegir lote…" : "Sin lotes disponibles") : "Elige producto primero"} />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="__none__">— sin lote —</SelectItem>
                                {lotes.map(lot => (
                                  <SelectItem key={lot.id} value={lot.id}>
                                    {lot.numero_lote || "s/n"} · {lot.bodega_nombre || lot.bodega_id?.slice(0,6)} · disp: {lot.cantidad_disponible ?? 0}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="col-span-2">
                            <Label className="text-[10px]">Cantidad</Label>
                            <Input type="number" min="0" step="1" value={l.cantidad}
                              onChange={(e) => updateLineaManual(idx, { cantidad: e.target.value })}
                              className="h-8 text-xs text-right tabular-nums"
                              data-testid={`nc-manual-cant-${idx}`} />
                          </div>
                          <div className="col-span-2">
                            <Label className="text-[10px]">Precio unit.</Label>
                            <Input type="number" min="0" step="0.01" value={l.precio_unitario}
                              onChange={(e) => updateLineaManual(idx, { precio_unitario: e.target.value })}
                              className="h-8 text-xs text-right tabular-nums"
                              data-testid={`nc-manual-precio-${idx}`} />
                          </div>
                          <div className="col-span-1 flex justify-end">
                            <Button size="icon" variant="ghost" onClick={() => removeLineaManual(idx)} className="text-[#b53a25] h-8 w-8" data-testid={`nc-manual-del-${idx}`}>
                              <Trash2 size={13} />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            <div>
              <Label>Cuenta contable haber</Label>
              <select value={form.cuenta_contable_id || ""} onChange={(e) => setForm(f => ({...f, cuenta_contable_id: e.target.value}))} className="h-9 px-2 rounded border border-[#d1dbd6] text-sm bg-white w-full">
                <option value="">— (usa default config) —</option>
                {cuentas.filter(c => c.tipo === "activo" || c.tipo === "egreso").slice(0, 200).map(c => (
                  <option key={c.id} value={c.id}>{c.codigo} · {c.nombre}</option>
                ))}
              </select>
            </div>
            <div className="col-span-2 grid grid-cols-4 gap-3 border-t pt-3">
              <div>
                <Label>Subtotal</Label>
                <Input
                  type="number" step="0.01"
                  value={form.subtotal}
                  onChange={(e) => setForm(f => ({...f, subtotal: e.target.value}))}
                  disabled={!!editing || form.modo === "producto"}
                  data-testid="nc-subtotal"
                />
              </div>
              <div>
                <Label>IVA %</Label>
                <Select
                  value={String(form.iva_porc)}
                  onValueChange={(v) => setForm(f => ({...f, iva_porc: v}))}
                  disabled={!!editing}
                >
                  <SelectTrigger data-testid="nc-iva-porc-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {IVA_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>IVA $</Label>
                <Input
                  type="number" step="0.01"
                  value={form.iva}
                  onChange={(e) => setForm(f => ({...f, iva: e.target.value}))}
                  disabled={!!editing || form.modo === "producto"}
                  data-testid="nc-iva"
                />
              </div>
              <div>
                <Label>Total *</Label>
                <Input
                  type="number" step="0.01"
                  value={form.total}
                  onChange={(e) => setForm(f => ({...f, total: e.target.value}))}
                  disabled={!!editing || form.modo === "producto"}
                  data-testid="nc-total"
                />
              </div>
            </div>
            <div className="col-span-2">
              <Label>Observaciones</Label>
              <Textarea value={form.observaciones || ""} onChange={(e) => setForm(f => ({...f, observaciones: e.target.value}))} rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} className="bg-[#205c45] hover:bg-[#1a4a37]" data-testid="save-nc-prov-btn">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
